import React, { useState } from "react";
import axios from "axios";
import { Input, Checkbox, Typography, Button } from "../../design-system";
import CityLocation from "./CityLocation";
import StateLocation from "./StateLocation";
import { stateLocationReq } from "../../api/branches";
import { getCities } from "../../api/address";

export default function Address(props) {
  // Use props.data as our source of truth.
  const address = props.data || {};

  const [searchPostalCode, setSearchPostalCode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorCep, setErrorCep] = useState(null);
  const [errorCity, setErrorCity] = useState(null);
  const [snChecked, setSnChecked] = useState(address.street_number === "S/N");

  function onChange(newFields) {
    const newAddress = { ...address, ...newFields };
    if (props.onChange) {
      props.onChange(newAddress);
    }
  }

  function handleInputChange(e) {
    onChange({ [e.target.name]: e.target.value });
  }

  function handleChangePostalCode(e) {
    const rawValue = e.target.value;
    const value = rawValue.replace(/\D+/g, "");

    onChange({ postal_code: value });
    setErrorCep(null);
    setErrorCity(null);

    if (searchPostalCode && value.length === 8) {
      setLoading(true);
      axios
        .get(`https://viacep.com.br/ws/${value}/json`)
        .then((cepResp) => {
          if (cepResp.data && !cepResp.data.erro) {
            const { cep, logradouro, bairro, localidade, uf, complemento } = cepResp.data;

            return stateLocationReq({ uf }).then((stateResp) => {
              const foundState = stateResp[0];
              if (foundState && localidade) {
                const normalizeAccents = (str) => str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";

                const fetchAndMatchCity = (queryStr) => {
                  return getCities({ city: queryStr, uf_code: foundState.uf_code })
                    .then((cities) => {
                      // Try with original string first, if empty and the unaccented version differs, try without accents
                      if (cities.length === 0 && normalizeAccents(queryStr) !== queryStr.toLowerCase()) {
                        return getCities({ city: normalizeAccents(queryStr), uf_code: foundState.uf_code });
                      }
                      return cities;
                    })
                    .then((cities) => {
                      const exactCity = cities.find(c => normalizeAccents(c.city_name) === normalizeAccents(localidade));
                      const bestCity = exactCity || (cities.length === 1 ? cities[0] : null);

                      if (!bestCity) {
                        setErrorCity(`Cidade '${localidade}' não encontrada. Selecione manualmente.`);
                      } else {
                        setErrorCity(null);
                      }
                      return bestCity;
                    });
                };

                return fetchAndMatchCity(localidade).then((bestCity) => {

                  onChange({
                    state_id: foundState.id,
                    state: foundState.state_name,
                    state_uf: foundState.uf_code,
                    city_id: bestCity ? bestCity.id : null,
                    city: bestCity ? bestCity.city_name : localidade,
                    postal_code: cep.replace(/\D+/g, "") || value,
                    street: logradouro || "",
                    district: bairro || "",
                    complement: complemento || "",
                  });
                });
              } else {
                onChange({
                  state_id: foundState ? foundState.id : null,
                  state: foundState ? foundState.state_name : "",
                  state_uf: foundState ? foundState.uf_code : "",
                  city_id: null,
                  city: localidade || "",
                  postal_code: cep.replace(/\D+/g, "") || value,
                  street: logradouro || "",
                  district: bairro || "",
                  complement: complemento || "",
                });
              }
            });
          }
          setErrorCep("CEP não encontrado");
        })
        .catch((err) => {
          console.error(err);
          setErrorCep("Erro ao buscar o CEP");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function toggleSn() {
    const isChecked = !snChecked;
    setSnChecked(isChecked);
    onChange({ street_number: isChecked ? "S/N" : "" });
  }

  function handleStateChange(stateId, stateName, ufCode) {
    setLoading(true);
    stateLocationReq({ uf_code: ufCode })
      .then((resp) => {
        const foundState = resp[0];
        if (foundState) {
          onChange({
            state_id: foundState.id,
            state: foundState.state_name,
            state_uf: foundState.uf_code,
            // When state changes, we clear city
            city_id: null,
            city: "",
          });
        }
      })
      .catch((err) => { })
      .finally(() => setLoading(false));
  }

  function handleCityChange(cityId, cityName) {
    onChange({ city_id: cityId, city: cityName });
    setErrorCity(null);
  }

  return (
    <div>
      <div className="row mb-3">
        <div className="col-md-6 mb-3">
          <div className="d-flex align-items-end">
            <div style={{ flex: 1 }}>
              <Input
                name="postal_code"
                type="cep"
                label="CEP"
                placeholder="00000-000"
                value={address.postal_code || ""}
                onChange={handleChangePostalCode}
                disabled={loading || props.loading || props.disabled}
                error={errorCep}
              />
            </div>
            <Button
              variant="secondary"
              style={{
                height: '42px',
                width: '42px',
                marginLeft: '8px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              disabled={loading || props.loading || props.disabled}
            >
              <i className="fa fa-search" />
            </Button>
          </div>
          <div style={{ marginTop: '8px' }}>
            <Checkbox
              disabled={loading || props.loading || props.disabled}
              onChange={() => setSearchPostalCode(!searchPostalCode)}
              checked={searchPostalCode}
              label="Pesquisa automática"
            />
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <Input
            name="street_number"
            label="Número"
            value={address.street_number || ""}
            onChange={handleInputChange}
            disabled={snChecked || loading || props.loading || props.disabled}
          />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-4 mb-3">
          <StateLocation
            selected={address.state_id}
            disabled={loading || props.loading || props.disabled}
            onChange={handleStateChange}
          />
        </div>

        <div className="col-md-4 mb-3">
          <CityLocation
            cityId={address.city_id}
            defaultInputValue={address.city}
            stateCode={address.state_uf} // wait, we don't have state_uf anymore
            onChange={handleCityChange}
            disabled={loading || props.loading || props.disabled || !address.state_id}
            error={errorCity}
          />
        </div>

        <div className="col-md-4 mb-3">
          <Input
            name="district"
            label="Bairro"
            value={address.district || ""}
            onChange={handleInputChange}
            disabled={loading || props.loading || props.disabled}
          />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6 mb-3">
          <Input
            name="street"
            label="Rua"
            value={address.street || ""}
            onChange={handleInputChange}
            disabled={loading || props.loading || props.disabled}
          />
        </div>

        <div className="col-md-6 mb-3">
          <Input
            name="complement"
            label="Complemento"
            value={address.complement || ""}
            onChange={handleInputChange}
            disabled={loading || props.loading || props.disabled}
          />
        </div>
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/_components/Address/index.jsx